;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:tutor\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"tutor\"],\"hasOptionalPrefix\":false},{\"path\":\"/:tutor/:404*\",\"regex\":\"^/([^/]+)/(.+)$\",\"paramNames\":[\"tutor\",\"404\"],\"hasOptionalPrefix\":false},{\"path\":\"/client/:publicId\",\"regex\":\"^/client/([^/]+)$\",\"paramNames\":[\"publicId\"],\"hasOptionalPrefix\":false},{\"path\":\"/invoice/:code\",\"regex\":\"^/invoice/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/mate/:alias\",\"regex\":\"^/mate/([^/]+)$\",\"paramNames\":[\"alias\"],\"hasOptionalPrefix\":false},{\"path\":\"/offer/:code\",\"regex\":\"^/offer/([^/]+)$\",\"paramNames\":[\"code\"],\"hasOptionalPrefix\":false},{\"path\":\"/quiz/:slug\",\"regex\":\"^/quiz/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/agent\"},{\"path\":\"/chatgpt\"},{\"path\":\"/claude\"},{\"path\":\"/client\"},{\"path\":\"/client-terms\"},{\"path\":\"/connect\"},{\"path\":\"/creator-terms\"},{\"path\":\"/dashboard\"},{\"path\":\"/dashboard/ambassador\"},{\"path\":\"/dashboard/referrals\"},{\"path\":\"/dashboard/settings\"},{\"path\":\"/dashboard/wallet\"},{\"path\":\"/home\"},{\"path\":\"/mcp\"},{\"path\":\"/new\"},{\"path\":\"/oauth/consent\"},{\"path\":\"/privacy\"},{\"path\":\"/terms\"},{\"path\":\"/trend\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.2";import * as Sentry from '@sentry/nextjs';

// --- Browser auto-translation crash guard ------------------------------------
// Google/Safari Translate rewrite and re-parent DOM text nodes (wrapping them in
// <font> tags). When React later removes or moves a node the translator detached,
// the native removeChild/insertBefore throws NotFoundError, surfacing as React
// #418/#310 and tripping the error boundary — which blanks the whole page.
// Tolerate the translator's mutations instead of crashing. Runs before hydration,
// in every environment. See https://github.com/facebook/react/issues/11538.
declare global {
  interface Node {
    __mmTranslateGuard?: boolean;
  }
}
if (typeof Node === 'function' && Node.prototype && !Node.prototype.__mmTranslateGuard) {
  Node.prototype.__mmTranslateGuard = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    // The translator re-parented this node; React's bookkeeping is stale. No-op safely.
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    // Reference node was re-parented by the translator; append instead of throwing.
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
// -----------------------------------------------------------------------------

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;

const SENTRY_DSN =
  'https://f26ee1e29247f283be58ea00416f7357@o4507159780589568.ingest.de.sentry.io/4507159802478672';

// Only initialize Sentry when NEXT_PUBLIC_ENV is explicitly set (sandbox/production)
// This prevents local development from sending data to Sentry
if (process.env.NEXT_PUBLIC_ENV) {
  Sentry.init({
    dsn: SENTRY_DSN,

    tracesSampleRate: 0.2,

    integrations: [
      Sentry.browserTracingIntegration({
        // Enable navigation and page load instrumentation for Next.js
        instrumentNavigation: true,
        instrumentPageLoad: true,
      }),
      // Send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],

    // Enable logs to be sent to Sentry
    enableLogs: true,

    initialScope: {
      tags: {
        service: 'frontend',
        environment: process.env.NEXT_PUBLIC_ENV,
      },
    },

    beforeSend(event, hint) {
      // Extract error for filtering and context
      const error = hint.originalException;

      // Ignore WebView-specific noise (iOS and Android in-app browsers)
      if (error instanceof Error) {
        if (
          error.message?.includes('_AutofillCallbackHandler') ||
          error.message?.includes('window.webkit.messageHandlers') ||
          error.message?.includes('Java object is gone')
        ) {
          return null;
        }
      }

      // Also check for string errors
      if (
        typeof error === 'string' &&
        (error.includes('_AutofillCallbackHandler') ||
          error.includes('window.webkit.messageHandlers') ||
          error.includes('Java object is gone'))
      ) {
        return null;
      }

      // Add additional context to all events
      event.tags = {
        ...event.tags,
        service: 'frontend',
        environment: process.env.NEXT_PUBLIC_ENV,
      };

      // Flag events that fired while the page was being auto-translated — the root
      // cause of DOM-mutation crashes (#418/#310/removeChild) — so they self-label.
      if (typeof document !== 'undefined') {
        const html = document.documentElement;
        event.tags.browser_translated =
          html.classList.contains('translated-ltr') ||
          html.classList.contains('translated-rtl') ||
          !!document.querySelector('font[style*="vertical-align"]');
      }

      // Add browser info
      if (typeof window !== 'undefined') {
        event.contexts = {
          ...event.contexts,
          browser_info: {
            userAgent: window.navigator.userAgent,
            language: window.navigator.language,
            platform: window.navigator.platform,
            cookieEnabled: window.navigator.cookieEnabled,
            onLine: window.navigator.onLine,
            windowSize: `${window.innerWidth}x${window.innerHeight}`,
            screenSize: `${window.screen.width}x${window.screen.height}`,
          },
          page_info: {
            url: window.location.href,
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            referrer: document.referrer,
          },
        };
      }

      // Extract and add more detailed error information
      const errorObj = hint.originalException;
      if (errorObj instanceof Error) {
        event.contexts = {
          ...event.contexts,
          error_details: {
            name: errorObj.name,
            message: errorObj.message,
            stack: errorObj.stack,
            cause: errorObj.cause ? String(errorObj.cause) : undefined,
          },
        };

        // Special handling for fetch errors
        if (errorObj.message.includes('fetch') || errorObj.message.includes('Failed to fetch')) {
          event.tags = {
            ...event.tags,
            error_type: 'fetch_error',
          };
          event.fingerprint = ['fetch-error', event.request?.url || 'unknown'];
        }
      }

      return event;
    },

    environment: process.env.NEXT_PUBLIC_ENV,
  });
}
